import { useState, useEffect, useCallback, useRef } from "react";
import localforage from "localforage";
import { getApiUrl } from "../utils/apiConfig";

// Configure localforage for offline storage
localforage.config({
  name: "SubtitleEditor",
  storeName: "autosave_drafts",
});

const useAutoSave = (
  projectId,
  subtitleIndex,
  translationData,
  options = {}
) => {
  const {
    enableOfflineStorage = true,
    onSaveSuccess,
    onSaveError,
    onOfflineStorage,
  } = options;

  const [lastSaved, setLastSaved] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [offlineDrafts, setOfflineDrafts] = useState([]);
  const [saveStatus, setSaveStatus] = useState("unsaved"); // Track save status more explicitly

  // Use refs to avoid recreating functions and prevent memory leaks
  const handleOnlineRef = useRef(() => setIsOnline(true));
  const handleOfflineRef = useRef(() => setIsOnline(false));
  const syncInProgressRef = useRef(false);

  // Track online/offline status with proper cleanup
  useEffect(() => {
    const handleOnline = handleOnlineRef.current;
    const handleOffline = handleOfflineRef.current;

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Validation helper
  const validateInputs = useCallback((data, projectId, subtitleIndex) => {
    if (!data) {
      throw new Error("Data is required for saving");
    }
    if (
      !projectId ||
      !Number.isInteger(Number(projectId)) ||
      Number(projectId) <= 0
    ) {
      throw new Error("Valid projectId is required");
    }
    if (
      subtitleIndex === undefined ||
      subtitleIndex === null ||
      !Number.isInteger(Number(subtitleIndex)) ||
      Number(subtitleIndex) < 0
    ) {
      throw new Error("Valid subtitleIndex is required");
    }
    return true;
  }, []);

  // Get auth token
  const getAuthToken = useCallback(() => {
    const token = localStorage.getItem("token");
    return token ? `Bearer ${token}` : null;
  }, []);

  // Save draft to server
  const saveDraftToServer = useCallback(
    async (data) => {
      const token = getAuthToken();
      if (!token) {
        throw new Error("No authentication token available");
      }
      const apiBaseUrl = await getApiUrl("");

      const response = await fetch(`${apiBaseUrl}/api/autosave/draft`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({
          projectId: parseInt(projectId),
          subtitleIndex: parseInt(subtitleIndex),
          draftText: data.draftText,
          originalText: data.originalText,
          hasErrors: data.hasErrors || false,
          errorDetails: data.errorDetails,
          sessionId: data.sessionId || `session_${Date.now()}`,
          isCompleted: data.isCompleted || false,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to save draft");
      }

      return await response.json();
    },
    [projectId, subtitleIndex, getAuthToken]
  );

  // Save draft to offline storage
  const saveDraftOffline = useCallback(
    async (data) => {
      if (!enableOfflineStorage) return;

      const offlineKey = `draft_${projectId}_${subtitleIndex}`;
      const offlineData = {
        ...data,
        projectId: parseInt(projectId),
        subtitleIndex: parseInt(subtitleIndex),
        savedAt: new Date().toISOString(),
        isOffline: true,
      };

      await localforage.setItem(offlineKey, offlineData);

      // Update offline drafts list
      const existingDrafts =
        (await localforage.getItem("offline_drafts_list")) || [];
      const updatedDrafts = existingDrafts.filter((d) => d.key !== offlineKey);
      updatedDrafts.push({ key: offlineKey, ...offlineData });

      await localforage.setItem("offline_drafts_list", updatedDrafts);
      setOfflineDrafts(updatedDrafts);

      if (onOfflineStorage) {
        onOfflineStorage(offlineData);
      }
    },
    [projectId, subtitleIndex, enableOfflineStorage, onOfflineStorage]
  );

  // Main save function - now only for manual saves
  const saveTranslationDraft = useCallback(
    async (data) => {
      try {
        validateInputs(data, projectId, subtitleIndex);
      } catch (validationError) {
        console.error("Validation error:", validationError);
        setSaveError(validationError.message);
        setSaveStatus("error");
        if (onSaveError) {
          onSaveError(validationError);
        }
        return;
      }

      setIsSaving(true);
      setSaveError(null);
      setSaveStatus("saving");

      try {
        if (isOnline) {
          // Try to save to server
          const result = await saveDraftToServer(data);
          setLastSaved(new Date());
          setSaveStatus("saved");

          // Remove from offline storage if it exists
          const offlineKey = `draft_${projectId}_${subtitleIndex}`;
          await localforage.removeItem(offlineKey);

          if (onSaveSuccess) {
            onSaveSuccess(result);
          }
        } else {
          // Save offline
          await saveDraftOffline(data);
          setLastSaved(new Date());
          setSaveStatus("offline");
        }
      } catch (error) {
        console.error("Auto-save error:", error);
        setSaveError(error.message);
        setSaveStatus("error");

        // Fallback to offline storage if server save fails
        if (isOnline && enableOfflineStorage) {
          try {
            await saveDraftOffline(data);
            setLastSaved(new Date());
            setSaveStatus("offline"); // Mark as offline save, not successful
            console.log("Successfully saved to offline storage as fallback");
          } catch (offlineError) {
            console.error("Offline save error:", offlineError);
            setSaveStatus("error");
          }
        }

        if (onSaveError) {
          onSaveError(error);
        }
      } finally {
        setIsSaving(false);
      }
    },
    [
      projectId,
      subtitleIndex,
      isOnline,
      saveDraftToServer,
      saveDraftOffline,
      onSaveSuccess,
      onSaveError,
      validateInputs,
      enableOfflineStorage,
    ]
  );

  // Sync offline drafts when coming back online
  useEffect(() => {
    const syncOfflineDrafts = async () => {
      if (!isOnline || !enableOfflineStorage || syncInProgressRef.current)
        return;

      syncInProgressRef.current = true;
      console.log("Starting offline draft sync...");

      try {
        const offlineDraftsList =
          (await localforage.getItem("offline_drafts_list")) || [];
        const successfulSyncs = [];
        const failedSyncs = [];

        for (const draft of offlineDraftsList) {
          try {
            await saveDraftToServer(draft);
            successfulSyncs.push(draft.key);
            console.log(`Successfully synced draft: ${draft.key}`);
          } catch (error) {
            console.error(`Failed to sync offline draft ${draft.key}:`, error);
            failedSyncs.push(draft);
          }
        }

        // Remove successfully synced drafts from individual storage
        for (const key of successfulSyncs) {
          await localforage.removeItem(key);
        }

        // Update offline drafts list with only failed syncs
        await localforage.setItem("offline_drafts_list", failedSyncs);
        setOfflineDrafts(failedSyncs);

        if (successfulSyncs.length > 0) {
          console.log(
            `Successfully synced ${successfulSyncs.length} offline drafts`
          );
        }
        if (failedSyncs.length > 0) {
          console.log(
            `${failedSyncs.length} drafts failed to sync and remain offline`
          );
        }
      } catch (error) {
        console.error("Error syncing offline drafts:", error);
      } finally {
        syncInProgressRef.current = false;
      }
    };

    if (isOnline) {
      syncOfflineDrafts();
    }
  }, [isOnline, enableOfflineStorage, saveDraftToServer]);

  // Load offline drafts on mount
  useEffect(() => {
    const loadOfflineDrafts = async () => {
      if (!enableOfflineStorage) return;

      try {
        const drafts = (await localforage.getItem("offline_drafts_list")) || [];
        setOfflineDrafts(drafts);
      } catch (error) {
        console.error("Error loading offline drafts:", error);
      }
    };

    loadOfflineDrafts();
  }, [enableOfflineStorage]);

  // Manual save function
  const manualSave = useCallback(() => {
    if (translationData) {
      saveTranslationDraft(translationData);
    }
  }, [translationData, saveTranslationDraft]);

  // Get draft recovery data
  const getDraftRecovery = useCallback(async () => {
    if (!enableOfflineStorage) return null;

    try {
      const offlineKey = `draft_${projectId}_${subtitleIndex}`;
      const offlineDraft = await localforage.getItem(offlineKey);

      if (offlineDraft) {
        return {
          ...offlineDraft,
          isRecovered: true,
          recoveredAt: new Date().toISOString(),
        };
      }

      return null;
    } catch (error) {
      console.error("Error getting draft recovery:", error);
      return null;
    }
  }, [projectId, subtitleIndex, enableOfflineStorage]);

  // Clear draft
  const clearDraft = useCallback(async () => {
    if (!enableOfflineStorage) return;

    try {
      const offlineKey = `draft_${projectId}_${subtitleIndex}`;
      await localforage.removeItem(offlineKey);

      // Update offline drafts list
      const existingDrafts =
        (await localforage.getItem("offline_drafts_list")) || [];
      const updatedDrafts = existingDrafts.filter((d) => d.key !== offlineKey);
      await localforage.setItem("offline_drafts_list", updatedDrafts);
      setOfflineDrafts(updatedDrafts);
    } catch (error) {
      console.error("Error clearing draft:", error);
    }
  }, [projectId, subtitleIndex, enableOfflineStorage]);

  // Save on entry change function
  const saveOnEntryChange = useCallback(
    (entryData) => {
      if (entryData && !isSaving) {
        saveTranslationDraft(entryData);
      }
    },
    [saveTranslationDraft, isSaving]
  );

  return {
    // State
    lastSaved,
    isSaving,
    saveError,
    isOnline,
    offlineDrafts,
    saveStatus,

    // Functions
    manualSave,
    saveOnEntryChange,
    getDraftRecovery,
    clearDraft,

    // Status helpers
    canSave:
      !isSaving && translationData && projectId && subtitleIndex !== undefined,
    hasOfflineDrafts: offlineDrafts.length > 0,
    isSyncInProgress: syncInProgressRef.current,
  };
};

export default useAutoSave;
